import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { AppFrame } from "@/components/AppFrame";
import { Eyebrow } from "@/components/primitives/Eyebrow";
import { Heading } from "@/components/primitives/Heading";
import { Hairline } from "@/components/primitives/Hairline";
import { LiveBar } from "@/components/primitives/LiveBar";
import { Timer } from "@/components/primitives/Timer";
import { LiveDot } from "@/components/primitives/LiveDot";
import type { Topic } from "@offsite/shared";

/**
 * Beacon Voting-Stage (`/workshop/voting`).
 * Brief 04 Screen 04 — VCLVoting.
 *
 * Eyebrow „WORKSHOP · VOTING · LIVE", Heading „Woran wollen wir heute arbeiten?",
 * 4 Topic-Karten als Listen-Zeilen mit LiveBar, Timer rechts oben (side-Größe).
 */

interface TopicTally {
  topic: Topic;
  votes: number;
}

export function VotingStage() {
  const { state } = useStore();
  const token = state.auth.token;
  const phase = state.workshop.phase;
  const [tallies, setTallies] = useState<TopicTally[]>([]);
  const [totalVotes, setTotalVotes] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [topicsRes, tallyRes] = await Promise.all([
          api.topics.list(token),
          api.votes.tallies(token),
        ]);
        if (cancelled) return;
        const tallyMap = new Map<string, number>(
          (tallyRes.tallies ?? []).map((t) => [t.topicId, t.count]),
        );
        const sum = Array.from(tallyMap.values()).reduce((a, b) => a + b, 0);
        setTallies(
          topicsRes.topics.map((t) => ({
            topic: t,
            votes: tallyMap.get(t.id) ?? 0,
          })),
        );
        setTotalVotes(sum);
        setTotalUsers(Math.max(sum, 64));
      } catch {
        /* swallow — polling will retry */
      }
    };
    load();
    const id = setInterval(load, 2000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token]);

  const max = Math.max(1, ...tallies.map((t) => t.votes));
  // Tie-Break-aware: highlight ALLE Topics, die das Max teilen.
  const winners = new Set(
    tallies.filter((t) => t.votes === max && max > 0).map((t) => t.topic.id),
  );
  const pctParticipation = totalUsers > 0 ? Math.round((totalVotes / totalUsers) * 100) : 0;

  return (
    <AppFrame skin="presenter" hideHeader hideFooter>
      <section
        style={{
          flex: 1,
          padding: "var(--s-7)",
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: "var(--s-7)",
          alignItems: "start",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--s-6)" }}>
          <header className="rise-1" style={{ display: "flex", flexDirection: "column", gap: "var(--s-3)" }}>
            <Eyebrow accent>WORKSHOP · VOTING · LIVE</Eyebrow>
            <Heading level="display">
              Woran wollen wir heute arbeiten<em style={{ fontStyle: "normal", color: "var(--accent-blue-soft)" }}>?</em>
            </Heading>
          </header>

          <Hairline skin="presenter" />

          <ol
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "var(--s-5)",
            }}
          >
            {tallies.map((t, idx) => {
              const isWinner = winners.has(t.topic.id);
              return (
                <li
                  key={t.topic.id}
                  className={`rise-${Math.min(6, idx + 2)}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto",
                    columnGap: "var(--s-5)",
                    alignItems: "center",
                  }}
                >
                  <span
                    className="mono"
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 14,
                      letterSpacing: "var(--tracking-meta)",
                      textTransform: "uppercase",
                      color: "var(--ink-300)",
                      minWidth: 80,
                    }}
                  >
                    TOPIC {String.fromCharCode(65 + idx)}
                  </span>
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--s-2)" }}>
                    <span
                      style={{
                        fontFamily: "var(--font-display)",
                        fontSize: "var(--t-h3)",
                        fontWeight: isWinner ? 700 : 500,
                        color: isWinner ? "var(--svo-weiss, #fff)" : "var(--ink-200)",
                        letterSpacing: "var(--tracking-h)",
                      }}
                    >
                      {t.topic.title}
                    </span>
                    <LiveBar value={t.votes} max={max} winner={isWinner} skin="presenter" height={6} />
                  </div>
                  <span
                    className="mono"
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 24,
                      fontWeight: 500,
                      color: isWinner ? "var(--svo-weiss, #fff)" : "var(--ink-200)",
                      minWidth: 60,
                      textAlign: "right",
                    }}
                  >
                    {t.votes}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Side-Panel: Timer + Live-Status */}
        <aside
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--s-5)",
            alignItems: "flex-end",
            minWidth: 220,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "var(--s-2)" }}>
            <LiveDot live />
            <Eyebrow accent>LIVE</Eyebrow>
          </div>
          {phase.timerEndsAt ? (
            <Timer deadline={phase.timerEndsAt} size="side" />
          ) : (
            <span
              className="mono"
              style={{
                fontFamily: "var(--font-mono)",
                fontVariantNumeric: "tabular-nums",
                fontSize: 64,
                fontWeight: 500,
                color: "var(--ink-400)",
                letterSpacing: "var(--tracking-display)",
                lineHeight: 1,
              }}
            >
              00<span style={{ opacity: 0.4 }}>:</span>00
            </span>
          )}
          <Hairline skin="presenter" />
          <span
            className="mono"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              letterSpacing: "var(--tracking-meta)",
              textTransform: "uppercase",
              color: "var(--ink-300)",
            }}
          >
            {totalVotes} / {totalUsers} STIMMEN
          </span>
          <span
            className="mono"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              letterSpacing: "var(--tracking-meta)",
              textTransform: "uppercase",
              color: "var(--ink-400)",
            }}
          >
            {pctParticipation}% BETEILIGUNG
          </span>
        </aside>
      </section>
    </AppFrame>
  );
}
